import { useCallback, useMemo, useEffect, useState } from 'react'
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  type NodeMouseHandler,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useQuery } from '@tanstack/react-query'

import { MindMapGroupNode } from './MindMapGroupNode'
import { MindMapDocNode } from './MindMapDocNode'
import { MindMapConceptNode } from './MindMapConceptNode'
import { MindMapEdge } from './MindMapEdge'
import { MindMapToolbar } from './MindMapToolbar'
import { MindMapDetailPanel } from './MindMapDetailPanel'
import { computeMindMapLayout, type TrackerData, type MindMapData } from './mindmap-layout'

const nodeTypes = {
  mindmapGroup: MindMapGroupNode,
  mindmapDoc: MindMapDocNode,
  mindmapConcept: MindMapConceptNode,
}

const edgeTypes = {
  mindmapEdge: MindMapEdge,
}

function MindMapCanvasInner() {
  const { fitView } = useReactFlow()

  // State
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set())
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null)
  const [searchFilter, setSearchFilter] = useState('')
  const [isExtracting, setIsExtracting] = useState(false)

  // Data fetching
  const { data: tracker } = useQuery<TrackerData>({
    queryKey: ['tracker'],
    queryFn: async () => {
      const resp = await fetch('/api/tracker')
      if (!resp.ok) throw new Error('Failed to load tracker')
      return resp.json()
    },
    staleTime: 60000,
  })

  const { data: mindmap, refetch: refetchMindmap } = useQuery<MindMapData>({
    queryKey: ['mindmap'],
    queryFn: async () => {
      const resp = await fetch('/api/mindmap')
      if (!resp.ok) throw new Error('Failed to load mindmap')
      return resp.json()
    },
    staleTime: 60000,
  })

  // Compute layout
  const { nodes: layoutNodes, edges: layoutEdges } = useMemo(
    () => computeMindMapLayout(tracker || null, mindmap || null, expandedGroups, expandedDocs, searchFilter, hoveredNodeId),
    [tracker, mindmap, expandedGroups, expandedDocs, searchFilter, hoveredNodeId],
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutEdges)

  // Sync layout changes
  useEffect(() => {
    setNodes(layoutNodes)
    setEdges(layoutEdges)
  }, [layoutNodes, layoutEdges, setNodes, setEdges])

  // Fit view when expand state changes
  useEffect(() => {
    const timer = setTimeout(() => fitView({ padding: 0.15, duration: 300 }), 50)
    return () => clearTimeout(timer)
  }, [expandedGroups.size, expandedDocs.size, fitView])

  // Node click handler
  const handleNodeClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      const id = node.id

      if (id.startsWith('group-')) {
        const groupId = id.replace('group-', '')
        setExpandedGroups((prev) => {
          const next = new Set(prev)
          if (next.has(groupId)) {
            next.delete(groupId)
            // Collapse all docs in this group too
            if (tracker) {
              for (const doc of tracker.documents) {
                if ((doc.seriesGroup || 'standalone-wp') === groupId) {
                  setExpandedDocs((prevDocs) => {
                    const nextDocs = new Set(prevDocs)
                    nextDocs.delete(doc.id)
                    return nextDocs
                  })
                }
              }
            }
          } else {
            next.add(groupId)
          }
          return next
        })
        setSelectedNodeId(id)
      } else if (id.startsWith('doc-')) {
        const docId = id.replace('doc-', '')
        const docConcepts = mindmap?.documents[docId]?.concepts || []
        if (docConcepts.length > 0) {
          setExpandedDocs((prev) => {
            const next = new Set(prev)
            if (next.has(docId)) next.delete(docId)
            else next.add(docId)
            return next
          })
        }
        setSelectedNodeId(id)
      } else if (id.startsWith('concept-')) {
        setSelectedNodeId(id)
      }
    },
    [tracker, mindmap],
  )

  // Node hover handlers
  const handleNodeMouseEnter: NodeMouseHandler = useCallback((_event, node) => {
    // For doc nodes, use the docId for hover connections
    if (node.id.startsWith('doc-')) {
      setHoveredNodeId(node.id.replace('doc-', ''))
    } else {
      setHoveredNodeId(node.id)
    }
  }, [])

  const handleNodeMouseLeave: NodeMouseHandler = useCallback(() => {
    setHoveredNodeId(null)
  }, [])

  // Canvas click clears selection
  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null)
  }, [])

  // Toolbar actions
  const handleExpandAll = useCallback(() => {
    if (!tracker) return
    const allGroups = new Set<string>()
    for (const doc of tracker.documents) {
      allGroups.add(doc.seriesGroup || 'standalone-wp')
    }
    setExpandedGroups(allGroups)
  }, [tracker])

  const handleCollapseAll = useCallback(() => {
    setExpandedGroups(new Set())
    setExpandedDocs(new Set())
    setSelectedNodeId(null)
  }, [])

  const handleRefresh = useCallback(async () => {
    setIsExtracting(true)
    try {
      const response = await fetch('/api/mindmap/extract-all', { method: 'POST' })
      const reader = response.body?.getReader()
      if (!reader) return

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // Process complete SSE events
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (line.startsWith('event: complete')) {
            // Extraction done
            break
          }
        }
      }

      await refetchMindmap()
    } finally {
      setIsExtracting(false)
    }
  }, [refetchMindmap])

  // Stats
  const stats = useMemo(() => {
    if (!tracker) return { groups: 0, documents: 0, concepts: 0, crossRefs: 0 }
    const groups = new Set(tracker.documents.map((d) => d.seriesGroup || 'standalone-wp'))
    return {
      groups: groups.size,
      documents: tracker.documents.length,
      concepts: mindmap?.meta.totalConcepts || 0,
      crossRefs: mindmap?.crossReferences.length || 0,
    }
  }, [tracker, mindmap])

  return (
    <div className="relative w-full h-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.1}
        maxZoom={3}
        className="bg-white dark:bg-gray-950"
        proOptions={{ hideAttribution: true }}
      >
        <Controls className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !shadow-sm" />
        <MiniMap
          className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700"
          nodeColor="#6B7280"
          maskColor="rgba(0,0,0,0.1)"
        />
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#E5E7EB" />
      </ReactFlow>

      <MindMapToolbar
        searchFilter={searchFilter}
        onSearchChange={setSearchFilter}
        onExpandAll={handleExpandAll}
        onCollapseAll={handleCollapseAll}
        onRefresh={handleRefresh}
        isExtracting={isExtracting}
        stats={stats}
      />

      <MindMapDetailPanel
        nodeId={selectedNodeId}
        tracker={tracker || null}
        mindmap={mindmap || null}
        onClose={() => setSelectedNodeId(null)}
      />
    </div>
  )
}

export function MindMapCanvas() {
  return (
    <ReactFlowProvider>
      <MindMapCanvasInner />
    </ReactFlowProvider>
  )
}
